import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UNSAFE_LocationContext, UNSAFE_NavigationContext } from 'react-router-dom';

const randomKey = () => Math.random().toString(36).slice(2, 10);

const parsePath = (path) => {
    let pathname = path;
    let search = '';
    let hash = '';
    const hashIndex = pathname.indexOf('#');
    if (hashIndex >= 0) {
        hash = pathname.substring(hashIndex);
        pathname = pathname.substring(0, hashIndex);
    }
    const searchIndex = pathname.indexOf('?');
    if (searchIndex >= 0) {
        search = pathname.substring(searchIndex);
        pathname = pathname.substring(0, searchIndex);
    }
    return { pathname: pathname || '/', search, hash };
};

const toLocation = (to, state = null) => {
    if (typeof to === 'string') {
        return { ...parsePath(to), state, key: randomKey() };
    }
    return {
        pathname: to.pathname || '/',
        search: to.search || '',
        hash: to.hash || '',
        state: to.state ?? state,
        key: to.key || randomKey(),
    };
};

const createMemoryHistory = (initialEntries) => {
    const entries = (initialEntries && initialEntries.length ? initialEntries : ['/']).map((e) =>
        toLocation(e),
    );
    let index = entries.length - 1;
    let listener = null;

    const emit = (action) => {
        if (listener) listener({ action, location: entries[index] });
    };

    return {
        get action() {
            return 'POP';
        },
        get location() {
            return entries[index];
        },
        createHref(to) {
            if (typeof to === 'string') return to;
            const { pathname = '/', search = '', hash = '' } = to;
            return `${pathname}${search}${hash}`;
        },
        encodeLocation(to) {
            const loc = toLocation(to);
            return {
                pathname: loc.pathname,
                search: loc.search,
                hash: loc.hash,
            };
        },
        push(to, state) {
            const next = toLocation(to, state);
            entries.splice(index + 1, entries.length - index - 1, next);
            index = entries.length - 1;
            emit('PUSH');
        },
        replace(to, state) {
            const next = toLocation(to, state);
            entries[index] = next;
            emit('REPLACE');
        },
        go(delta) {
            const nextIndex = Math.max(0, Math.min(entries.length - 1, index + delta));
            if (nextIndex === index) return;
            index = nextIndex;
            emit('POP');
        },
        listen(fn) {
            listener = fn;
            return () => {
                listener = null;
            };
        },
    };
};

export default function IsolatedRouter({ children, initialEntries = ['/'], basename = '/' }) {
    const historyRef = useRef(null);
    if (!historyRef.current) {
        historyRef.current = createMemoryHistory(initialEntries);
    }
    const history = historyRef.current;

    const [state, setState] = useState(() => ({
        location: history.location,
        navigationType: history.action,
    }));

    useLayoutEffect(() => {
        return history.listen((update) => {
            setState({ location: update.location, navigationType: update.action });
        });
    }, [history]);

    const navigator = useMemo(
        () => ({
            createHref: history.createHref.bind(history),
            encodeLocation: history.encodeLocation.bind(history),
            push: history.push.bind(history),
            replace: history.replace.bind(history),
            go: history.go.bind(history),
        }),
        [history],
    );

    const navigationContextValue = useMemo(
        () => ({
            basename: basename.replace(/^\/*/, '/'),
            navigator,
            static: false,
            future: {},
        }),
        [basename, navigator],
    );

    const locationContextValue = useMemo(
        () => ({
            location: state.location,
            navigationType: state.navigationType,
        }),
        [state.location, state.navigationType],
    );

    return (
        <UNSAFE_NavigationContext.Provider value={navigationContextValue}>
            <UNSAFE_LocationContext.Provider value={locationContextValue}>
                {children}
            </UNSAFE_LocationContext.Provider>
        </UNSAFE_NavigationContext.Provider>
    );
}
